"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { motion } from "framer-motion";
import { cn } from "lib/utils";

function Tabs({ className, ...props }) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col", className)}
      {...props}
    />
  );
}

function TabsList({ className, children, ...props }) {
  const [activeRect, setActiveRect] = React.useState(null);
  const listRef = React.useRef(null);

  // Watch for active trigger change
  React.useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-state="active"]');
    if (active) {
      const rect = active.getBoundingClientRect();
      const parentRect = listRef.current.getBoundingClientRect();
      setActiveRect({
        left: rect.left - parentRect.left,
        width: rect.width,
      });
      return;
    }

    setActiveRect(null);
  });

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      className={cn(
        "relative flex items-center gap-6 border-b border-border",
        className,
      )}
      {...props}
    >
      {children}
      {activeRect && (
        <motion.div
          className="absolute bottom-0 h-0.5 bg-primary rounded-full"
          initial={false}
          animate={{ x: activeRect.left, width: activeRect.width }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        />
      )}
    </TabsPrimitive.List>
  );
}

function TabsTrigger({ className, ...props }) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "relative inline-flex items-center justify-center px-2 py-2 text-sm font-medium text-muted-foreground transition-colors",
        "hover:text-foreground",
        "data-[state=active]:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
